import React, { useState, useEffect, useRef } from 'react';
import { Workflow, Project } from '../types';
import { Plus, FolderOpen, Trash2, Clock, Image, Film, Music, MoreVertical, Pencil, Check, X } from 'lucide-react';
import { RetryImage } from './flow/nodes/parts/content/RetryImage';

const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";

interface ProjectManagerProps {
    workflows: Workflow[];
    projects?: Project[];
    onSelectWorkflow: (id: string) => void;
    onCreateProject: (title: string) => void;
    onDeleteWorkflow: (id: string) => void;
    onRenameWorkflow: (id: string, newTitle: string) => void;
}

interface MenuPosition {
    top: number;
    left: number;
}

export const ProjectManager: React.FC<ProjectManagerProps> = ({
    workflows,
    projects,
    onSelectWorkflow,
    onCreateProject,
    onDeleteWorkflow,
    onRenameWorkflow
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    
    const displayProjects = projects || workflows.map(w => ({
        id: w.id,
        name: w.title,
        workflow_data: { nodes: w.nodes, connections: w.connections, groups: w.groups },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        thumbnail: w.thumbnail
    }));
    const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0 });
    const [showNewProjectModal, setShowNewProjectModal] = useState(false);
    const [newProjectTitle, setNewProjectTitle] = useState('');
    const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const handleOpenMenu = (id: string) => {
        const button = menuButtonRefs.current[id];
        if (button) {
            const rect = button.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 4,
                left: rect.right - 120
            });
        }
        setMenuOpenId(id);
    };

    const handleRename = (id: string, currentTitle: string) => {
        setEditingId(id);
        setEditingTitle(currentTitle);
        setMenuOpenId(null);
    };

    const handleSaveRename = (id: string) => {
        if (editingTitle.trim()) {
            onRenameWorkflow(id, editingTitle.trim());
        }
        setEditingId(null);
        setEditingTitle('');
    };

    const handleDelete = (id: string) => {
        onDeleteWorkflow(id);
        setMenuOpenId(null);
    };

    const handleOpenNewProject = () => {
        setNewProjectTitle('');
        setShowNewProjectModal(true);
    };

    const handleCreateProject = () => {
        if (newProjectTitle.trim()) {
            onCreateProject(newProjectTitle.trim());
            setShowNewProjectModal(false);
            setNewProjectTitle('');
        }
    };

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getWorkflowIcon = (workflow: Workflow | Project) => {
        const nodes = ('workflow_data' in workflow ? workflow.workflow_data?.nodes : workflow.nodes) || [];
        const hasVideo = nodes.some(n => n.type === 'VIDEO_GENERATOR' || n.type === 'VIDEO_ANALYZER');
        const hasAudio = nodes.some(n => n.type === 'AUDIO_GENERATOR');
        const hasImage = nodes.some(n => n.type === 'IMAGE_GENERATOR' || n.type === 'IMAGE_EDITOR');
        
        if (hasVideo) return <Film size={16} className="text-purple-400" />;
        if (hasAudio) return <Music size={16} className="text-emerald-400" />;
        if (hasImage) return <Image size={16} className="text-cyan-400" />;
        return <FolderOpen size={16} className="text-zinc-400" />;
    };

    return (
        <div className="w-full h-full overflow-hidden bg-[#0a0a0c]">
            <div className="w-full h-full overflow-hidden text-slate-200 selection:bg-cyan-500/30">
                <div className="absolute inset-0 noise-bg" />
                
                <div className="absolute inset-0 flex flex-col items-center justify-center px-4 py-8">
                    <div className="flex flex-col items-center justify-center mb-12 select-none animate-in fade-in slide-in-from-bottom-8 duration-1000">
                        <div className="relative">
                            <img 
                                src="/BONGAI.svg" 
                                alt="BONG·AI" 
                                className="w-80 md:w-[420px] h-auto drop-shadow-sm"
                            />
                            <div className="absolute -inset-10 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-blue-500/20 blur-[60px] opacity-20 pointer-events-none mix-blend-screen"></div>
                        </div>
                        <div className="flex items-center gap-4 mt-4">
                            <div className="h-px w-12 bg-gradient-to-r from-transparent to-zinc-600"></div>
                            <span className="text-[11px] font-bold tracking-[0.6em] text-zinc-500 uppercase">Welcome</span>
                            <div className="h-px w-12 bg-gradient-to-l from-transparent to-zinc-600"></div>
                        </div>
                    </div>

                    <div className="w-full max-w-4xl px-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-zinc-300 flex items-center gap-2">
                                <FolderOpen size={20} className="text-zinc-500" />
                                我的项目
                            </h2>
                            <button
                                onClick={handleOpenNewProject}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-medium transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 hover:-translate-y-0.5 duration-300"
                            >
                                <Plus size={18} />
                                新建项目
                            </button>
                        </div>

                        {displayProjects.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
                                <div className="w-20 h-20 rounded-2xl bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center mb-4">
                                    <FolderOpen size={32} className="text-zinc-600" />
                                </div>
                                <p className="text-sm mb-2">暂无项目</p>
                                <p className="text-xs text-zinc-600">点击上方按钮创建您的第一个项目</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {displayProjects.map((project) => (
                                    <div
                                        key={project.id}
                                        className="group relative bg-zinc-900/50 border border-zinc-800/50 rounded-2xl hover:border-zinc-700/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20 cursor-pointer"
                                        onClick={() => {
                                            if (!editingId) {
                                                onSelectWorkflow(project.id);
                                            }
                                        }}
                                    >
                                        <div className="aspect-video bg-zinc-800/30 relative overflow-hidden rounded-t-2xl">
                                            {(project as any).thumbnail ? (
                                                <RetryImage
                                                    src={(project as any).thumbnail}
                                                    alt={project.name}
                                                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                                                    fallback={
                                                        <div className="w-full h-full flex items-center justify-center bg-zinc-800/30">
                                                            <Image size={24} className="text-white/20 animate-pulse" />
                                                        </div>
                                                    }
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800/50 to-zinc-900/50">
                                                    {getWorkflowIcon(project)}
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent opacity-60" />
                                        </div>
                                        
                                        <div className="p-4">
                                            {editingId === project.id ? (
                                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="text"
                                                        value={editingTitle}
                                                        onChange={(e) => setEditingTitle(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveRename(project.id);
                                                            if (e.key === 'Escape') setEditingId(null);
                                                        }}
                                                        className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500"
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => handleSaveRename(project.id)}
                                                        className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingId(null)}
                                                        className="p-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white transition-colors"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-sm font-medium text-zinc-200 truncate group-hover:text-white transition-colors">
                                                            {project.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2 mt-1.5 text-xs text-zinc-500">
                                                            <Clock size={12} />
                                                            <span>{formatDate((project as any).updated_at || (project as any).created_at)}</span>
                                                            <span className="text-zinc-700">·</span>
                                                            <span>{(project as any).node_count ?? (project.workflow_data?.nodes || []).length} 个节点</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="relative z-[160]" onClick={e => e.stopPropagation()}>
                                                        <button
                                                            ref={(el) => { menuButtonRefs.current[project.id] = el; }}
                                                            onClick={(e) => { e.stopPropagation(); handleOpenMenu(project.id); }}
                                                            className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-all opacity-0 group-hover:opacity-100"
                                                        >
                                                            <MoreVertical size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {menuOpenId && (
                <>
                    <div 
                        className="fixed inset-0 z-[150]" 
                        onClick={() => setMenuOpenId(null)}
                    />
                    <div 
                        className="fixed bg-zinc-800 border border-zinc-700 rounded-xl shadow-xl z-[200] min-w-[120px]"
                        style={{ top: menuPosition.top, left: menuPosition.left }}
                    >
                        <button
                            onClick={() => handleRename(menuOpenId, workflows.find(w => w.id === menuOpenId)?.title || '')}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors rounded-t-xl"
                        >
                            <Pencil size={14} />
                            重命名
                        </button>
                        <button
                            onClick={() => handleDelete(menuOpenId)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors rounded-b-xl"
                        >
                            <Trash2 size={14} />
                            删除
                        </button>
                    </div>
                </>
            )}

            {showNewProjectModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">新建项目</h3>
                            <div className="mb-4">
                                <label className="block text-sm text-zinc-400 mb-2">项目名称</label>
                                <input
                                    type="text"
                                    value={newProjectTitle}
                                    onChange={(e) => setNewProjectTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleCreateProject();
                                        if (e.key === 'Escape') setShowNewProjectModal(false);
                                    }}
                                    placeholder="请输入项目名称"
                                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500 transition-colors"
                                    autoFocus
                                />
                            </div>
                            <div className="flex items-center gap-3 justify-end">
                                <button
                                    onClick={() => setShowNewProjectModal(false)}
                                    className="px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white text-sm font-medium transition-all"
                                >
                                    取消
                                </button>
                                <button
                                    onClick={handleCreateProject}
                                    disabled={!newProjectTitle.trim()}
                                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    创建
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};